import { type INode } from '@shared/lib/ris.types'
import Image from 'next/image'
import Link from 'next/link'
import RefLink from './RefLink'

export default function Inline({
  value,
  children,
  type,
  skipInitialGldsym = false,
  skipInitialSymbol = false,
  richRefLinks = true,
  ...props
}: INode & {
  skipInitialGldsym?: boolean
  skipInitialSymbol?: boolean
  richRefLinks?: boolean
}) {
  if (type === 'link') {
    if (props.href) {
      // Some RIS documents have actual links in them as type "link". If so, render them as normal links
      return <Link href={props.href}> {value}</Link>
    }

    const ref = props.refs?.[0]
    if (!ref.value) return null

    return <RefLink refLink={ref} hover={richRefLinks} />
  }

  if (typeof value === 'string') {
    // Use a template literal here as to prevent empty comments being added to the HTML, in order to reduce payload size (https://github.com/facebook/react/issues/14725)
    return ` ${value}`
  }
  if (type === 'binary') {
    const src =
      typeof children?.[0] === 'string' ? children[0] : children?.[0]?.value

    return (
      <Image
        src={`https://www.ris.bka.gv.at${src ?? ''}`}
        width={Number(props.width)}
        height={Number(props.height)}
        alt={props.alt ?? ''}
      />
    )
  }

  const firstChild = children?.[0]
  if (children?.length === 1 && typeof firstChild === 'string') {
    return firstChild
  }
  if (
    children?.length === 1 &&
    firstChild &&
    typeof firstChild !== 'string' &&
    firstChild.value
  ) {
    // Optimization to prevent additional mapping
    return ` ${firstChild.value}`
  }

  return (
    children?.map((item, index) => {
      if (typeof item === 'string') return item
      if (index === 0 && skipInitialGldsym && item.type === 'gldsym')
        return null
      if (index === 0 && skipInitialSymbol && item.type === 'symbol')
        return null

      return <Inline key={index} {...item} richRefLinks={richRefLinks} />
    }) ?? []
  )
}
